import React, { useCallback, useEffect, useState } from 'react';
import { Layers, Upload, GitCompareArrows, FileBarChart } from 'lucide-react';
import { fundAPI, dematAPI } from '../../services/api';
import { BenposUploadTab } from '../../components/demat/BenposUploadTab';
import { ReconciliationTab } from '../../components/demat/ReconciliationTab';
import { DematReportsTab } from '../../components/demat/DematReportsTab';

const TABS = [
  { id: 'upload', label: 'Upload & Map', icon: Upload },
  { id: 'recon', label: 'Reconciliation', icon: GitCompareArrows },
  { id: 'reports', label: 'Reports', icon: FileBarChart },
];

export default function Dematerialisation() {
  const [tab, setTab] = useState('upload');
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState('');
  const [uploads, setUploads] = useState([]);
  const [uploadId, setUploadId] = useState('');
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  useEffect(() => { fundAPI.getAll().then((r) => setFunds(r.data || [])).catch(() => setFunds([])); }, []);

  const loadUploads = useCallback(async (selectId) => {
    if (!fundId) { setUploads([]); setUploadId(''); return; }
    const { data } = await dematAPI.list(fundId);
    const list = data.uploads || [];
    setUploads(list);
    setUploadId(selectId || list[0]?.id || '');
  }, [fundId]);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  useEffect(() => {
    if (!uploadId) { setRecon(null); return; }
    setReconLoading(true);
    dematAPI.reconciliation(uploadId).then((r) => setRecon(r.data)).catch(() => setRecon(null)).finally(() => setReconLoading(false));
  }, [uploadId]);

  const onUploaded = async (doc) => { await loadUploads(doc.id); setTab('recon'); };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="demat-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-teal-600 text-white"><Layers size={20} /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dematerialisation</h1>
            <p className="text-sm text-gray-500 mt-1">Reconcile ISIN-wise units held per the portal with the RTA&apos;s BenPos (Beneficiary Position) report.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white min-w-[220px]" data-testid="demat-fund-select">
              <option value="">-- Select a Fund --</option>
              {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          {tab !== 'upload' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">BenPos Snapshot</label>
              <select value={uploadId} onChange={(e) => setUploadId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white min-w-[240px]" data-testid="demat-snapshot-select" disabled={!uploads.length}>
                {!uploads.length && <option value="">No uploads yet</option>}
                {uploads.map((u) => <option key={u.id} value={u.id}>As on {u.as_on_date} — {u.file_name}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`demat-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {!fundId ? (
        <div className="bg-white border rounded-lg p-12 text-center text-gray-500" data-testid="demat-select-fund-hint">Select a fund to get started.</div>
      ) : tab === 'upload' ? (
        <BenposUploadTab fundId={fundId} uploads={uploads} onUploaded={onUploaded} onChanged={() => loadUploads()} onOpen={(id) => { setUploadId(id); setTab('recon'); }} />
      ) : reconLoading ? (
        <div className="p-12 text-center text-gray-500">Reconciling…</div>
      ) : !recon ? (
        <div className="bg-white border rounded-lg p-12 text-center text-gray-500" data-testid="demat-no-snapshot">Upload a BenPos report to see the reconciliation.</div>
      ) : tab === 'recon' ? (
        <ReconciliationTab recon={recon} />
      ) : (
        <DematReportsTab recon={recon} />
      )}
    </div>
  );
}
